import { Text, View, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import Unicon from '@/components/General/Unicon';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { LegendList } from "@legendapp/list";
import { useRouter } from 'expo-router';
import { useCallback } from 'react';
import { ActivityIndicator } from 'react-native-paper';

const TrainingProgressTab = ({ personID }) => {
    const router = useRouter();
    const { data: progress, isLoading } = Query.trainingProgress.byPersonId({ personID });

    const getStatusColor = (status) => {
        switch (status) {
            case "ENROLLED": return { bg: "primaryContainer", text: "primary" };
            case "COMPLETED": return { bg: "successContainer", text: "success" };
            case "GRADUATED": return { bg: "successContainer", text: "success" };
            case "DROPPED": return { bg: "errorContainer", text: "error" };
            case "UNENROLLED": return { bg: "surfaceVariant", text: "textSecondary" };
            default: return { bg: "surface", text: "onSurface" };
        }
    };

    const renderItem = useCallback(({ item }) => {
        const statusColors = getStatusColor(item.enrollmentStatus);
        const percent = Number(item.completionPercent) || 0;

        return (
            <Pressable onPress={() => router.push("/training/" + item.moduleID + "/" + personID)} style={styles.card}>
                <View style={styles.cardHeader}>
                    <View style={styles.cardHeaderLeft}>
                        <View style={styles.iconCircle}>
                            <Unicon name="school-outline" size={20} colorVariant="primary" />
                        </View>
                        <View style={styles.cardHeaderText}>
                            <Text style={styles.moduleName} numberOfLines={1}>{item.moduleName}</Text>
                            <View style={styles.metaRow}>
                                <View style={styles.statusBadge(statusColors.bg)}>
                                    <Text style={styles.statusBadgeText(statusColors.text)}>{item.enrollmentStatus}</Text>
                                </View>
                            </View>
                        </View>
                    </View>
                    <Text style={styles.percentText(percent)}>{percent}%</Text>
                </View>

                <View style={styles.progressBarBg}>
                    <View style={styles.progressBarFill(percent)} />
                </View>

                <View style={styles.detailRow}>
                    <View style={styles.detailItem}>
                        <Unicon name="layers-outline" size={14} colorVariant="textSecondary" />
                        <Text style={styles.detailText}>{item.classesCompleted}/{item.totalClasses} classes</Text>
                    </View>
                    <View style={styles.detailItem}>
                        <Unicon name="checkmark-circle-outline" size={14} colorVariant="textSecondary" />
                        <Text style={styles.detailText}>{item.requirementsCompleted}/{item.totalRequirements} requirements</Text>
                    </View>
                    {item.enrolledAt && (
                        <View style={styles.detailItem}>
                            <Unicon name="calendar-outline" size={14} colorVariant="textSecondary" />
                            <Text style={styles.detailText}>Enrolled {new Date(item.enrolledAt).toLocaleDateString()}</Text>
                        </View>
                    )}
                </View>

                {item.completedAt && (
                    <View style={styles.completedRow}>
                        <Unicon name="checkmark-done-circle-outline" size={16} colorVariant="success" />
                        <Text style={styles.completedText}>Completed {new Date(item.completedAt).toLocaleDateString()}</Text>
                    </View>
                )}
            </Pressable>
        );
    }, [router, personID]);

    if (isLoading) {
        return <View style={styles.loadingContainer}><ActivityIndicator size="large" /></View>;
    }

    if (!progress || progress.length === 0) {
        return <EmptyListPlaceholder icon="school-outline" message="Not enrolled in any training modules" />;
    }

    return (
        <LegendList
            recycleItems
            keyExtractor={(item) => item.enrollmentID}
            estimatedItemSize={140}
            data={progress}
            renderItem={renderItem}
            contentContainerStyle={styles.listPadding}
            keyboardShouldPersistTaps="handled"
        />
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 40 },
    listPadding: { padding: 16, paddingBottom: 100 },
    card: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: spacing.md,
        marginBottom: spacing.sm,
        borderWidth: 1,
        borderColor: colors.border,
        _web: {
            transition: "all 0.15s ease",
            _hover: { backgroundColor: colors.hover }
        }
    },
    cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: spacing.sm,
    },
    cardHeaderLeft: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        flex: 1,
        gap: spacing.sm,
    },
    iconCircle: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: colors.primaryContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    cardHeaderText: { flex: 1 },
    moduleName: {
        fontSize: typography.body,
        fontFamily: fonts.heading,
        color: colors.text,
        marginBottom: 4,
    },
    metaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
        flexWrap: 'wrap',
    },
    statusBadge: (bg) => ({
        paddingVertical: 1,
        paddingHorizontal: 6,
        borderRadius: 4,
        backgroundColor: colors[bg],
    }),
    statusBadgeText: (text) => ({
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors[text],
    }),
    teacherChip: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    teacherChipText: {
        fontSize: 11,
        color: colors.textSecondary,
    },
    percentText: (percent) => ({
        fontSize: 18,
        fontFamily: fonts.heading,
        color: percent >= 100 ? colors.success : colors.primary,
        marginLeft: spacing.sm,
    }),
    progressBarBg: {
        height: 6,
        backgroundColor: colors.surfaceVariant,
        borderRadius: 3,
        marginBottom: spacing.sm,
    },
    progressBarFill: (percent) => ({
        height: 6,
        borderRadius: 3,
        width: `${Math.min(percent, 100)}%`,
        backgroundColor: percent >= 100 ? colors.success : percent >= 50 ? colors.primary : colors.warning,
    }),
    detailRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.md,
    },
    detailItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    detailText: {
        fontSize: 12,
        color: colors.textSecondary,
    },
    completedRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginTop: spacing.sm,
        paddingTop: spacing.sm,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    completedText: {
        fontSize: 12,
        color: colors.success,
        fontFamily: fonts.medium,
    },
}));

export default TrainingProgressTab;
